<script setup>
import { ref, onMounted } from 'vue';
import { useEventListener } from '@vueuse/core';
import { LocalStorage } from 'shared/helpers/localStorage';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

// Layout de duas colunas (detalhes + Historico em chat) compartilhado
// pelas paginas dedicadas de Contato, Empresa e Negociacao (spec 127
// v4 + v7). Largura padrao fixa (nao proporcional ao container - ver
// ContactManageView.vue v5.5 pra entender o porque), divisor arrastavel,
// escolha do usuario persistida por tipo de entidade (storage-key).
// Sem largura salva ainda, abre no maximo (pedido do dono, spec 127 v8):
// o painel esquerdo fica no tamanho ideal pro conteudo dele.
const props = defineProps({
  isLoading: { type: Boolean, default: false },
  hasData: { type: Boolean, default: false },
  storageKey: { type: String, required: true },
  minWidth: { type: Number, default: 280 },
  maxWidth: { type: Number, default: 560 },
});

const sidebarWidth = ref(null);
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

onMounted(() => {
  const storedWidth = Number(LocalStorage.get(props.storageKey));
  sidebarWidth.value = storedWidth || props.maxWidth;
});

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = event.clientX;
  startWidth.value = sidebarWidth.value;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const onResizeMove = event => {
  if (!isResizing.value) return;
  const delta = event.clientX - startX.value;
  sidebarWidth.value = Math.max(
    props.minWidth,
    Math.min(props.maxWidth, startWidth.value + delta)
  );
};

const onResizeEnd = () => {
  if (!isResizing.value) return;
  isResizing.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  LocalStorage.set(props.storageKey, sidebarWidth.value);
};

useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
</script>

<template>
  <div class="flex w-full h-full min-w-0 bg-n-surface-1">
    <div
      v-if="isLoading && !hasData"
      class="flex items-center justify-center w-full"
    >
      <SpinnerLoader :size="32" class="text-n-blue-9" />
    </div>
    <template v-else-if="hasData && sidebarWidth">
      <div :style="{ width: `${sidebarWidth}px` }" class="h-full shrink-0">
        <slot name="sidebar" />
      </div>
      <div
        class="w-1 shrink-0 cursor-col-resize hover:bg-n-brand relative z-10"
        :class="{ 'bg-n-brand': isResizing }"
        @mousedown="onResizeStart"
      />
      <div class="flex-1 min-w-0 h-full overflow-hidden">
        <slot name="timeline" />
      </div>
    </template>
  </div>
</template>
